import { useState, useRef, useEffect } from 'react'
import { apiUrl, authFetch } from '../../lib/api'

interface Props {
  selectedCode: string
  fullContent: string
  language: string
  position: { top: number; left: number }
  onApply: (newCode: string) => void
  onClose: () => void
}

export function InlineEditOverlay({ selectedCode, fullContent, language, position, onApply, onClose }: Props) {
  const [instruction, setInstruction] = useState('')
  const [result, setResult] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { inputRef.current?.focus() }, [])
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  const handleSubmit = async () => {
    if (!instruction.trim() || loading) return
    setLoading(true)
    try {
      const res = await authFetch(apiUrl('/api/chat'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [{ role: 'user', content: `Edit this ${language} code: ${instruction}\n\nSelected code:\n${selectedCode}\n\nReturn ONLY the replacement code, no explanation, no markdown backticks, no FILE: headers.` }],
          context: { mode: 'edit' },
        }),
      })
      const data = await res.json()
      let newCode = data.text ?? ''
      newCode = newCode.replace(/^```\w*\n?/, '').replace(/\n?```$/, '').trim()
      setResult(newCode)
    } catch { setResult(null) }
    finally { setLoading(false) }
  }

  const oldLines = selectedCode.split('\n')
  const newLines = (result ?? '').split('\n')
  const maxLen = Math.max(oldLines.length, newLines.length)

  // Clamp position to viewport
  const top = Math.min(Math.max(position.top, 10), window.innerHeight - 350)
  const left = Math.min(Math.max(position.left, 10), window.innerWidth - 420)

  return (
    <div style={{
      position: 'fixed', top, left, zIndex: 1000, width: 400,
      background: 'var(--bg-1)', border: '1px solid var(--accent)',
      borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.5)', overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ padding: '8px 12px', background: 'var(--bg-2)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 6 }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
        </svg>
        <span style={{ fontSize: 10, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>INLINE EDIT</span>
        <span style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginLeft: 'auto' }}>Ctrl+K</span>
      </div>

      {/* Input */}
      <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 6 }}>
        <input
          ref={inputRef}
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit() }}
          placeholder="Describe the change..."
          style={{
            flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border-2)',
            background: 'var(--bg-0)', color: 'var(--text-0)', fontFamily: 'var(--font-mono)',
            fontSize: 12, outline: 'none',
          }}
        />
        <button onClick={handleSubmit} disabled={loading || !instruction.trim()} style={{
          padding: '6px 14px', borderRadius: 6, border: 'none',
          background: loading ? 'var(--bg-3)' : 'var(--accent)',
          color: '#fff', cursor: loading ? 'wait' : 'pointer',
          fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700,
        }}>
          {loading ? '...' : 'Edit'}
        </button>
      </div>

      {/* Diff */}
      {result !== null && (
        <>
          <div style={{ maxHeight: 220, overflow: 'auto', background: 'var(--bg-0)' }}>
            <pre style={{ margin: 0, fontFamily: 'var(--font-mono)', fontSize: 11, lineHeight: 1.7 }}>
              {Array.from({ length: maxLen }).map((_, i) => {
                const o = oldLines[i]
                const n = newLines[i]
                if (o === n) return <div key={i} style={{ padding: '0 12px', color: 'var(--text-1)' }}><span style={{ display: 'inline-block', width: 16, color: 'var(--text-2)', userSelect: 'none' }}> </span>{o}</div>
                return (
                  <div key={i}>
                    {o !== undefined && <div style={{ padding: '0 12px', background: 'rgba(240,112,112,0.12)', color: 'var(--red)' }}><span style={{ display: 'inline-block', width: 16, userSelect: 'none' }}>-</span>{o}</div>}
                    {n !== undefined && <div style={{ padding: '0 12px', background: 'rgba(95,212,164,0.12)', color: 'var(--accent-2)' }}><span style={{ display: 'inline-block', width: 16, userSelect: 'none' }}>+</span>{n}</div>}
                  </div>
                )
              })}
              {newLines.slice(oldLines.length).map((line, i) => (
                <div key={`extra-${i}`} style={{ padding: '0 12px', background: 'rgba(95,212,164,0.12)', color: 'var(--accent-2)' }}><span style={{ display: 'inline-block', width: 16, userSelect: 'none' }}>+</span>{line}</div>
              ))}
            </pre>
          </div>

          {/* Actions */}
          <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border)', display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={rejectBtn}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              Reject
            </button>
            <button onClick={() => { onApply(result); onClose() }} style={acceptBtn}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              Accept
            </button>
          </div>
        </>
      )}
    </div>
  )
}

const rejectBtn: React.CSSProperties = {
  padding: '5px 14px', borderRadius: 6, border: '1px solid var(--red)',
  background: 'rgba(240,112,112,0.08)', color: 'var(--red)', cursor: 'pointer',
  fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
  display: 'flex', alignItems: 'center', gap: 4,
}
const acceptBtn: React.CSSProperties = {
  padding: '5px 14px', borderRadius: 6, border: '1px solid var(--accent-2)',
  background: 'rgba(95,212,164,0.08)', color: 'var(--accent-2)', cursor: 'pointer',
  fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
  display: 'flex', alignItems: 'center', gap: 4,
}
